import React, { ReactElement, useMemo } from 'react'
import styles from './EupheusPage.module.scss'
import { Screenshot } from '@/components/static/images/Screenshot/Screenshot'
import { SplashPageCTA } from '@/components/static/SplashPageSharedComponents/SplashPageCTA/SplashPageCTA'
import { BackToTop } from '@/components/static/BackToTop/BackToTop'
import { LinkItem } from '@/components/static/ui/LinkItem/LinkItem'
import { UiLayoutConstant } from '@/globals/uiLayoutConstant'
import { EUPHEUS_FAQ } from '@/components/static/EupheusPage/eupheusFaq'
import { AskEupheusImage } from '@/components/static/images/owlBranding/askEupheusImage'

export const EupheusPage: React.FC<{ isLoggedIn: boolean }> = (
    props
): ReactElement => {
    const faqStructuredData = useMemo((): string => {
        return JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: EUPHEUS_FAQ.map((entry) => ({
                '@type': 'Question',
                name: entry.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: entry.answer,
                },
            })),
        })
    }, [])

    return (
        <div className={styles.informationPage}>
            {/* eslint-disable-next-line react/no-danger */}
            <script
                type={'application/ld+json'}
                dangerouslySetInnerHTML={{ __html: faqStructuredData }}
            />
            <div className={styles.informationPageTop}>
                {/* Hero: the owl beside the heading, not a lone image above
                    a centred title. The lightbulb owl is the "ask Eupheus"
                    illustration already used in the product. */}
                <div className={styles.eupheusHero}>
                    <div className={styles.eupheusHeroImage}>
                        <AskEupheusImage />
                    </div>
                    <div className={styles.eupheusHeroText}>
                        <h1>Meet Eupheus, Your AI Study Partner</h1>
                        <p className={styles.eupheusLede}>
                            Eupheus is the AI study partner built into Just
                            Enough Prep. Ask it anything about your FSA Group
                            &amp; Health exam and it answers by researching your{' '}
                            <span className={styles.informationEmphasis}>
                                actual course materials
                            </span>{' '}
                            — the outlines, notecards, practice questions and
                            the underlying source material — then links every
                            source it used, so you can check its work.
                        </p>
                    </div>
                </div>

                <h2>It researches before it answers</h2>
                <p>
                    General chatbots answer from memory, which is how you end up
                    with a confident, plausible, completely invented formula.
                    Eupheus works the other way around: every question kicks off
                    a search through your course content first. Answers come
                    back with real formulas, worked examples as proper tables,
                    and a Sources list pointing at the exact sections behind
                    them.
                </p>
                <Screenshot
                    src={'/updates/20260819/formulaTableScreenshot.webp'}
                    alt={
                        'Eupheus explaining the credibility-weighted blended rate formula with a worked example table and cited sources'
                    }
                />

                <h2>Every claim links back to the syllabus</h2>
                <p>
                    Ask which notecards cover a topic, or how a concept tends to
                    be tested, and you get a study plan with the receipts —
                    including which past exam sittings a topic showed up in.
                    Everything Eupheus mentions is a link: tap through to the
                    notecard, outline section or question it is citing.
                </p>
                <Screenshot
                    src={'/updates/20260819/examStrategyScreenshot.webp'}
                    alt={
                        'Eupheus recommending which funding-method topics to prioritize, citing past exam sittings'
                    }
                />

                <h2>It knows where you are</h2>
                <p>
                    Ask about &quot;this notecard&quot; or &quot;this
                    problem&quot; and Eupheus sees the page you are on. Ask it
                    to take you somewhere — &quot;open the questions
                    dashboard&quot;, &quot;take me to that outline&quot; — and
                    it navigates for you. Your browser&apos;s Back button works
                    exactly as normal, and Eupheus only ever moves within Just
                    Enough Prep.
                </p>
                <Screenshot
                    src={'/updates/20260819/pageAwareScreenshot.webp'}
                    alt={
                        'Eupheus identifying the notecard on screen, giving a mnemonic, and navigating to the questions dashboard'
                    }
                />

                <h2>Conversations you can come back to</h2>
                <p>
                    Eupheus names each conversation automatically and keeps it,
                    so you can resume any thread later and pick up where you
                    left off — or clear out the ones you are done with.
                </p>
                <Screenshot
                    src={'/updates/20260819/conversationListScreenshot.webp'}
                    alt={
                        'the Eupheus conversation list with auto-generated titles and timestamps'
                    }
                />

                <h2>Autograding: any question, Excel included</h2>
                <p>
                    Eupheus grades every kind of practice question — conceptual,
                    mathematical, multiple choice, multi-part — and it grades a
                    whole quiz or exam in one click. You get points sub-part by
                    sub-part with a written justification for each one, concrete
                    suggestions for earning more points next time, and a full
                    grading history on every question.
                </p>
                <p>
                    Upload the workbook you built from our Excel template and it
                    gets graded cell by cell against the official solution.
                    Eupheus hands your spreadsheet back{' '}
                    <span className={styles.informationEmphasis}>
                        marked up
                    </span>
                    : problem cells highlighted in red with notes on what went
                    wrong and how to fix each one, plus a Grader Feedback
                    summary sheet.
                </p>
                <Screenshot
                    src={
                        '/updates/20260817/suggestionsDownloadsScreenshot.webp'
                    }
                    alt={
                        'autograde feedback with improvement suggestions and a marked-up workbook download'
                    }
                />

                <div className={styles.eupheusLimits}>
                    <h3>What Eupheus will not do</h3>
                    <p>
                        Eupheus is a study partner for your course, not a
                        general-purpose assistant. It will politely pass on
                        essay writing, coding homework and anything else
                        off-syllabus. And while it grounds every answer in your
                        course materials, it is an AI and it can still be wrong
                        — which is exactly why the sources stay one click away.
                        Check anything that matters.
                    </p>
                </div>

                <h2>Frequently asked questions</h2>
                {EUPHEUS_FAQ.map((entry) => (
                    <div key={entry.question}>
                        <h3 className={styles.eupheusFaqQuestion}>
                            {entry.question}
                        </h3>
                        <p className={styles.eupheusFaqAnswer}>
                            {entry.answer}
                        </p>
                    </div>
                ))}

                <p>
                    Eupheus ships with every Just Enough Prep course. See{' '}
                    <LinkItem
                        href={{ pathname: UiLayoutConstant.COURSES_PAGE }}
                        inline={true}
                    >
                        our exams and plans
                    </LinkItem>
                    , or read the{' '}
                    <LinkItem
                        href={{
                            pathname:
                                UiLayoutConstant.STUDY_MATERIALS_GUIDE_OTHER,
                        }}
                        inline={true}
                    >
                        study materials guide
                    </LinkItem>{' '}
                    to see how it fits into the rest of the platform.
                </p>
            </div>
            {!props.isLoggedIn && <SplashPageCTA />}
            <BackToTop isLoggedIn={props.isLoggedIn} />
        </div>
    )
}
